import { useState } from 'react';
import { line, Color, Size, t, type LoadOutCheckAnswer, type LoadOutCheckLine } from '@bms2/shared';
import { AlertBanner, Button, Dropdown, Pill } from '../../components/controls';
import { assetSearch, loadOutAcknowledgementsPath, loadOutPath } from '../assets/api';
import { post, useApi } from './api';
import styles from './RecordPage.module.css';
import bookingStyles from './VisitBooking.module.css';
import loadOutStyles from './WorkOrderLoadOut.module.css';

/** One line's key: the requirement and the worker it asks, or the visit's one Shared line. */
const keyOf = (row: Pick<LoadOutCheckLine, 'siteRequirementTypeId' | 'employmentId'>): string => `${row.siteRequirementTypeId}:${row.employmentId ?? '-'}`;

/**
 * 🔒 The Load-Out Check, asked on the Work Order page at dispatch (piece 28;
 * decisions 0151 and 9140; T2367). Booking a crew is the dispatch (decision
 * 4340), so the Visits tile draws this while a visit is Dispatched and none has
 * started. Each line is the site's or the checklist's equipment, a Personal one
 * asked of each worker by name and a Shared one once for the visit; a
 * serialized instrument asks which one goes on the truck, and saying yes issues
 * it to the worker until the visit's day. 🔨 It records that somebody said yes
 * and never holds the dispatch: the visit is already sent whatever is answered.
 */
export function WorkOrderLoadOut({ workOrderId, apiBaseUrl = '' }: { workOrderId: string; apiBaseUrl?: string }) {
  const read = useApi<LoadOutCheckAnswer>(loadOutPath(workOrderId), apiBaseUrl);
  const [picked, setPicked] = useState<Record<string, { id: string; label: string }>>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const answer = read.state.kind === 'ready' ? read.state.data : null;
  if (!answer) return null;
  // 🔨 A reply missing its lines reads as nothing to bring rather than crashing the tile.
  const lines = Array.isArray(answer.lines) ? answer.lines : [];
  const instruments = assetSearch('instrument', apiBaseUrl);

  const acknowledge = async (row: LoadOutCheckLine) => {
    const key = keyOf(row);
    if (busy) return;
    setBusy(key);
    const outcome = await post(loadOutAcknowledgementsPath(workOrderId), { siteRequirementTypeId: row.siteRequirementTypeId, employmentId: row.employmentId, assetNodeId: row.asksForAsset ? picked[key]?.id ?? null : null }, apiBaseUrl);
    setBusy(null);
    if (!outcome.ok) return setRefusal(outcome.message);
    setRefusal(null);
    read.reload?.();
  };

  return (
    <section className={loadOutStyles.check} data-load-out-check={workOrderId}>
      <h3 className={bookingStyles.heading}>{t('asset.loadOut.title')}</h3>
      <p className={styles.hint}>{t('asset.loadOut.hint')}</p>
      {lines.length === 0 ? <p className={styles.hint} data-load-out-none="true">{t('asset.loadOut.none')}</p> : (
        <ul className={loadOutStyles.lines}>
          {lines.map((row) => {
            const key = keyOf(row);
            const instrument = picked[key] ?? null;
            const waiting = row.asksForAsset && !instrument;
            return (
              <li key={key} className={loadOutStyles.line} data-load-out-line={row.name} data-load-out-worker={row.workerName ?? 'Shared'} data-load-out-acknowledged={row.acknowledged ? 'true' : 'false'}>
                <span>{line(row.name, '·', row.scope === 'Shared' ? t('asset.loadOut.shared') : row.workerName ?? t('asset.loadOut.unnamed'))}</span>{' '}
                {row.acknowledged ? (
                  <Pill text={t('asset.loadOut.acknowledged')} foreground="var(--status-good-text)" background="var(--status-good-bg)" size={Size.Small} />
                ) : (
                  <span className={loadOutStyles.answer}>
                    {row.asksForAsset ? (
                      // 🔨 The instrument on the truck is one the register already holds, picked by name, tag or serial; a node made here would carry neither, so no Add New row is offered.
                      <Dropdown size={Size.Medium} label={t('asset.loadOut.instrument')} entityLabel={t('asset.loadOut.instrumentEntity')} search={instruments} value={instrument?.label ?? null} onSelect={(chosen) => setPicked((was) => ({ ...was, [key]: { id: chosen.id, label: chosen.line1 } }))} onAddNew={null} minChars={0} />
                    ) : null}
                    <Button size={Size.Small} color={Color.Navy} text={[t('asset.loadOut.acknowledge')]} disabled={waiting || busy !== null} actions={[() => void acknowledge(row)]} />
                    {waiting ? <span className={styles.hint}>{t('asset.loadOut.pickFirst')}</span> : null}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
    </section>
  );
}
